import { PREFILLED_EMAIL_PARAM, PREFILLED_NEWSLETTER_PARAM } from './prefilled-email';

/**
 * A Google sign-up that Clerk says is not finished yet.
 *
 * 🔴 WE BOUNCED THESE PEOPLE AND NEVER RECORDED WHY. Back on the Google callback, Clerk sometimes hands
 * us a sign-up whose status is `missing_requirements` instead of `complete`. We sent every one of them
 * back to an empty sign-up page with a generic "could not finish with Google" toast, and logged only a
 * code that said it happened. Between 2026-08-24 and 2026-09-17, 601 people hit it and 45% never made
 * an account. Michael's Clerk change on 2026-09-18 (password optional) removed the biggest cause, but
 * the transfer failure (`sso_fallback_sign_up`) kept firing at the same ~5% of Google sign-ups, and
 * nothing tells us which field Clerk still wants.
 *
 * So this file does three things: it describes the half-finished sign-up for our analytics, it picks
 * the one recovery we can finish on the spot, and it builds a fallback page that keeps the
 * address Google already gave us.
 */

/** The parts of Clerk's sign-up resource this file reads. Structural: tests need no Clerk. */
export interface IncompleteSsoSignUp {
  emailAddress: string | null;
  missingFields: readonly string[];
  requiredFields: readonly string[];
  status: string | null;
  unverifiedFields: readonly string[];
}

/** A Clerk error as the new sign-up API returns it, rather than throwing it. */
export interface SsoTransferError {
  code?: string;
  message?: string;
}

/**
 * Event properties for the `auth_error` and recovery events. Comma-joined strings rather than arrays:
 * a Mixpanel breakdown then shows one row per combination ("email_address", "first_name,last_name").
 */
export interface IncompleteSsoSignUpDiagnostics {
  sign_up_missing_fields: string;
  sign_up_required_fields: string;
  sign_up_status: string;
  sign_up_unverified_fields: string;
  sso_transfer_error_code?: string;
  sso_transfer_error_message?: string;
}

/** Longest Clerk message we forward. Clerk's messages are short; the cap is against a surprise. */
const MAX_ERROR_MESSAGE_LENGTH = 200;

/** Recorded for an empty list: "nothing missing" must read differently from "not recorded". */
const EMPTY_FIELD_LIST = 'none';

function joinFields(fields: readonly string[] | null | undefined): string {
  return fields && fields.length > 0 ? [...fields].sort().join(',') : EMPTY_FIELD_LIST;
}

export function describeIncompleteSsoSignUp(
  signUp: IncompleteSsoSignUp,
  transferError?: SsoTransferError | null,
): IncompleteSsoSignUpDiagnostics {
  return {
    sign_up_status: signUp.status ?? 'none',
    sign_up_missing_fields: joinFields(signUp.missingFields),
    sign_up_unverified_fields: joinFields(signUp.unverifiedFields),
    sign_up_required_fields: joinFields(signUp.requiredFields),
    ...(transferError?.code ? { sso_transfer_error_code: transferError.code } : {}),
    ...(transferError?.message
      ? { sso_transfer_error_message: transferError.message.slice(0, MAX_ERROR_MESSAGE_LENGTH) }
      : {}),
  };
}

/**
 * - `verify_email`: the only thing left is proving the address, which our code screen already does.
 * - `email_fallback`: anything else. Back to sign-up, with the address filled in.
 */
export type SsoSignUpRecovery = 'verify_email' | 'email_fallback';

/**
 * Only the one case we can finish without asking for anything new: nothing is missing, and the email
 * address is the only unverified field. A missing name, password or legal checkbox would need a form
 * we do not have on this page, and those take the fallback.
 */
export function resolveSsoSignUpRecovery(signUp: IncompleteSsoSignUp): SsoSignUpRecovery {
  const onlyEmailUnverified = signUp.unverifiedFields.length === 1 && signUp.unverifiedFields[0] === 'email_address';

  if (
    signUp.status === 'missing_requirements' &&
    signUp.missingFields.length === 0 &&
    onlyEmailUnverified &&
    signUp.emailAddress
  ) {
    return 'verify_email';
  }

  return 'email_fallback';
}

/**
 * Where a Google sign-up we could not finish should land: sign-up, with the address Google gave us. The
 * email code is then one step away instead of a blank form.
 *
 * The newsletter choice rides along because the prefill opens the email form directly and skips the
 * chooser, where the checkbox lives; without it, a reader who declined would be silently opted in.
 */
export function ssoSignUpFallbackPath(options: {
  allowlistedParams: string;
  emailAddress: string | null;
  newsletter: boolean | null;
}): string {
  const params = new URLSearchParams(options.allowlistedParams);

  const email = options.emailAddress?.trim();
  if (email) {
    params.set(PREFILLED_EMAIL_PARAM, email);
    if (options.newsletter !== null) {
      params.set(PREFILLED_NEWSLETTER_PARAM, options.newsletter ? '1' : '0');
    }
  }

  const query = params.toString();
  return query ? `/sign-up?${query}` : '/sign-up';
}

/** The code screen, with the attribution params carried so the finished account keeps its UTMs. */
export function ssoSignUpVerifyEmailPath(allowlistedParams: string): string {
  const params = new URLSearchParams(allowlistedParams);
  params.set('step', 'email-verification');
  return `/sign-up?${params.toString()}`;
}

/** A Clerk verification as the sign-in and sign-up resources expose it. */
interface SsoVerification {
  error?: { code?: string } | null;
  status?: string | null;
}

/** The parts of Clerk's sign-in and sign-up this reads, to say what became of the Google attempt. */
export interface SsoAttemptState {
  signIn: { firstFactorVerification?: SsoVerification | null; id?: string | null; protectCheck?: unknown };
  signUp: {
    id?: string | null;
    protectCheck?: unknown;
    verifications?: { externalAccount?: SsoVerification | null } | null;
  };
}

export interface SsoAttemptDiagnostics {
  sign_in_exists: 'yes' | 'no';
  sign_in_google_error_code?: string;
  sign_in_google_status: string;
  sign_up_exists: 'yes' | 'no';
  sign_up_google_error_code?: string;
  sign_up_google_status: string;
  sso_protect_check: 'sign_in' | 'sign_up' | 'none';
}

/**
 * What became of the Google attempt, in Clerk's own terms, so a failure can be told apart:
 *
 * - no sign-in and no sign-up: the callback ran with no Google attempt in progress;
 * - a pending bot check (`sso_protect_check`): Clerk Protect stopped it, which Clerk's own callback
 *   handler routes to a challenge and ours did not look for;
 * - Google's verification status and error code, from whichever side holds it.
 *
 * #3036 recorded which sign-up fields were missing. That told us the failures were not missing fields; this
 * is meant to say what they are.
 *
 * 🔴 Why `sign_up_exists` matters: Clerk reports an EMPTY sign-up as `missing_requirements` (clerk-js 6.34:
 * `get status(){return this.#e_.status||"missing_requirements"}`), so `sign_up_status` alone cannot tell a
 * half-finished sign-up from no sign-up at all. 21 of the first 22 failures after #3036 were no sign-up.
 */
export function describeSsoAttempt({ signIn, signUp }: SsoAttemptState): SsoAttemptDiagnostics {
  const signInGoogle = signIn.firstFactorVerification;
  const signUpGoogle = signUp.verifications?.externalAccount;

  let protectCheck: SsoAttemptDiagnostics['sso_protect_check'] = 'none';
  if (signUp.protectCheck) {
    protectCheck = 'sign_up';
  } else if (signIn.protectCheck) {
    protectCheck = 'sign_in';
  }

  return {
    sign_in_exists: signIn.id ? 'yes' : 'no',
    sign_up_exists: signUp.id ? 'yes' : 'no',
    sign_in_google_status: signInGoogle?.status ?? 'none',
    sign_up_google_status: signUpGoogle?.status ?? 'none',
    sso_protect_check: protectCheck,
    ...(signInGoogle?.error?.code ? { sign_in_google_error_code: signInGoogle.error.code } : {}),
    ...(signUpGoogle?.error?.code ? { sign_up_google_error_code: signUpGoogle.error.code } : {}),
  };
}
